/** /tags/:id/:tab? — tag editor shell. Tabs: General, Data, Used by. */
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tagsApi from '@/api/tags';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { IdentitySubtitle } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { tagsHref } from '@/utils/gridLinks';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import { tagCategoryActions } from './tagLinks';
import { TagDataTab } from './tabs/TagDataTab';
import { TagGeneralTab } from './tabs/TagGeneralTab';
import { TagUsedByTab } from './tabs/TagUsedByTab';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'data', label: 'Data' },
  { id: 'used-by', label: 'Used by' },
] as const;

export function TagDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: tag,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tags', 'detail', id],
    queryFn: () => tagsApi.retrieve(id),
    enabled: Number.isFinite(id),
  });
  const onSaved = () => {
    void refetch();
    qc.invalidateQueries({ queryKey: ['tags'] });
  };

  const deleteMutation = useMutation({
    mutationFn: () => tagsApi.remove(id),
    onSuccess: () => {
      snackbar.show('Tag deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['tags'] });
      navigate('/tags');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const canViewHistory = hasPerm('console_audit_view');
  const tabs = useMemo(
    () => (canViewHistory ? [...TABS, { id: 'history', label: 'History' } as const] : [...TABS]),
    [canViewHistory],
  );
  const activeTab = useMemo(() => tabs.find((t) => t.id === tab)?.id ?? 'general', [tabs, tab]);

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid tag id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !tag) return <Alert severity="error">Couldn’t load tag.</Alert>;

  return (
    <Box>
      <PageHeader
        helpStory="tags-editor"
        section={tabs.find((t) => t.id === activeTab)?.label}
        title={tag.name}
        subtitle={
          <IdentitySubtitle
            tenant={tag.tenant}
            tenantFallback="System"
            grid={tag.tenant ? { href: tagsHref([], tag.tenant.id), label: 'Tags of this operator' } : null}
            id={tag.id}
            extra={[
              <ValueActions
                key="category"
                actions={tagCategoryActions(tag.category, { tenantId: tag.tenant?.id })}
              >
                {tag.category.name}
              </ValueActions>,
            ]}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" variant="outlined" label={tag.category.code} />
            <PermissionGate perm="console_tags_manage">
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigate(`/tags/${id}/${v}`)}>
          {tabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane show={activeTab === 'general'}>
        <TagGeneralTab tag={tag} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'data'}>
        <TagDataTab tag={tag} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'used-by'}>
        <TagUsedByTab tag={tag} />
      </TabPane>
      <TabPane show={activeTab === 'history'}>
        <HistoryPanel contentType="demo.group_tour_tag" objectPk={String(tag.id)} onRestored={onSaved} />
      </TabPane>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to={tagsHref([], tag.tenant?.id)} style={{ color: '#0a1a3f' }}>
          ← Back to tags
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this tag?"
        message={`"${tag.name}" will be deleted (soft delete — it can be restored).`}
        confirmLabel="Delete tag"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function TabPane({ show, children }: { show: boolean; children: ReactNode }) {
  if (!show) return null;
  return <Box>{children}</Box>;
}
